import { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { AdvertisingPrices, Advertisement } from '../../models/types';
import { toast } from 'react-toastify';
import { Upload, DollarSign, Calendar, Eye, Clock, Image as ImageIcon, Square, RectangleHorizontal } from 'lucide-react';
import { resizeLogoForBannerAdaptive, validateImageFile, getImageDimensions, LogoSpacing } from '../../utils/imageUtils';

const AdvertisingPage = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [processingImage, setProcessingImage] = useState(false);
  const [prices, setPrices] = useState<AdvertisingPrices>({
    oneWeek: 50,
    twoWeeks: 90,
    threeWeeks: 120
  });
  const [myAds, setMyAds] = useState<Advertisement[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [originalImageDimensions, setOriginalImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [logoSpacing, setLogoSpacing] = useState<LogoSpacing | null>(null);
  const [advertisingEnabled, setAdvertisingEnabled] = useState(true);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    websiteUrl: '',
    duration: 1 as 1 | 2 | 3,
    imageFile: null as File | null,
    processedImageUrl: '' as string
  });

  useEffect(() => {
    if (!currentUser || userData?.role !== 'provider') return;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch advertising prices
        const pricesDoc = await getDocs(collection(db, 'systemParams'));
        const pricesData = pricesDoc.docs.find(doc => doc.id === 'advertisingPrices');
        if (pricesData) {
          setPrices(pricesData.data() as AdvertisingPrices);
        }

        // Check if advertising is enabled
        const systemSettingsDoc = await getDoc(doc(db, 'systemParams', 'systemSettings'));
        if (systemSettingsDoc.exists()) {
          const settings = systemSettingsDoc.data();
          setAdvertisingEnabled(settings.advertisingEnabled !== false);
        }
        // Fetch user's advertisements
        const adsSnapshot = await getDocs(collection(db, 'advertisements'));
        const userAds = adsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt.toDate(),
            updatedAt: doc.data().updatedAt.toDate(),
            startDate: doc.data().startDate?.toDate(),
            endDate: doc.data().endDate?.toDate()
          }))
          .filter(ad => ad.providerId === currentUser.uid) as Advertisement[];
        
        setMyAds(userAds.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()));
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error(t('error.loading'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, userData, t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!validateImageFile(file)) {
      toast.error('Please select a valid image file (JPEG, PNG, WebP) under 5MB');
      return;
    }

    try {
      setProcessingImage(true);
      
      // Get original dimensions
      const dimensions = await getImageDimensions(file);
      setOriginalImageDimensions(dimensions);
      
      // Create preview of original image
      const originalPreview = URL.createObjectURL(file);
      setImagePreview(originalPreview);
      
      // Resize image for banner with adaptive spacing
      const { resizedImageUrl, spacing } = await resizeLogoForBannerAdaptive(file, dimensions);
      setLogoSpacing(spacing);
      
      setFormData(prev => ({
        ...prev,
        imageFile: file,
        processedImageUrl: resizedImageUrl
      }));

      const logoType = spacing.isSquare ? 'square' : 'rectangular';
      toast.success(`Image processed successfully! ${logoType} logo optimized for banner display.`);
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Error processing image. Please try again.');
    } finally {
      setProcessingImage(false);
    }
  };

  const calculatePrice = () => {
    switch (formData.duration) {
      case 1: return prices.oneWeek;
      case 2: return prices.twoWeeks;
      case 3: return prices.threeWeeks;
      default: return prices.oneWeek;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser || !userData) return;

    // Validation
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error(t('advertising.fillRequiredFields'));
      return;
    }

    if (formData.description.length > 200) {
      toast.error(t('advertising.descriptionTooLong'));
      return;
    }

    try {
      setSubmitting(true);

      // Get provider profile for company name
      const profileDoc = await getDoc(doc(db, 'providerProfiles', currentUser.uid));
      const companyName = profileDoc.exists() ? profileDoc.data().companyName : userData.username;

      const advertisementData = {
        providerId: currentUser.uid,
        providerName: userData.username,
        companyName,
        title: formData.title.trim(),
        description: formData.description.trim(),
        imageUrl: formData.processedImageUrl || null,
        websiteUrl: formData.websiteUrl.trim() || null,
        duration: formData.duration,
        status: 'pending',
        totalPrice: calculatePrice(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Store logo spacing information for banner display
        logoSpacing: logoSpacing || null
      };

      await addDoc(collection(db, 'advertisements'), advertisementData);
      
      toast.success(t('advertising.submittedSuccessfully'));
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        websiteUrl: '',
        duration: 1,
        imageFile: null,
        processedImageUrl: ''
      });
      setImagePreview(null);
      setOriginalImageDimensions(null);
      setLogoSpacing(null);

      // Refresh ads list
      window.location.reload();
    } catch (error) {
      console.error('Error submitting advertisement:', error);
      toast.error(t('error.saving'));
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'active': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      case 'expired': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (userData?.role !== 'provider') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-700 mb-2">{t('advertising.accessDenied')}</h2>
          <p className="text-red-600">{t('advertising.providersOnly')}</p>
        </div>
      </div>
    );
  }

  if (!advertisingEnabled) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-yellow-700 mb-2">Advertising Currently Unavailable</h2>
          <p className="text-yellow-600">The advertising feature is temporarily disabled. Please check back later.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <h1 className="text-2xl font-bold mb-6">{t('advertising.title')}</h1>

      {/* Pricing Information */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center">
          <div className="h-5 w-5 mr-2 text-primary-600 flex items-center justify-center font-bold">€</div>
          {t('advertising.pricingInfo')}
        </h2>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">€{prices.oneWeek}</div>
            <div className="text-sm text-gray-600">{t('advertising.oneWeek')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">€{prices.twoWeeks}</div>
            <div className="text-sm text-gray-600">{t('advertising.twoWeeks')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-primary-600">€{prices.threeWeeks}</div>
            <div className="text-sm text-gray-600">{t('advertising.threeWeeks')}</div>
          </div>
        </div>
      </div>

      {/* Advertisement Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-6">{t('advertising.createAdvertisement')}</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              {t('advertising.adTitle')} *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600"
              placeholder={t('advertising.titlePlaceholder')}
              required
            />
            <p className="text-xs text-gray-500 mt-1">{formData.title.length}/100 {t('profile.characters')}</p>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              {t('advertising.description')} * ({t('profile.maxCharacters')})
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              maxLength={200}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600"
              placeholder={t('advertising.descriptionPlaceholder')}
              required
            />
            <p className="text-xs text-gray-500 mt-1">{formData.description.length}/200 {t('profile.characters')}</p>
          </div>

          <div>
            <label htmlFor="websiteUrl" className="block text-sm font-medium text-gray-700 mb-1">
              {t('advertising.websiteUrl')} ({t('common.optional')})
            </label>
            <input
              type="url"
              id="websiteUrl"
              name="websiteUrl"
              value={formData.websiteUrl}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600"
              placeholder="https://www.example.com"
            />
          </div>

          <div>
            <label htmlFor="imageFile" className="block text-sm font-medium text-gray-700 mb-1">
              {t('advertising.image')} ({t('common.optional')})
            </label>
            
            {/* Image Upload Section */}
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <label className="flex items-center px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 transition-colors">
                  <Upload className="h-5 w-5 mr-2 text-gray-500" />
                  <span className="text-sm text-gray-700">{t('advertising.chooseImage')}</span>
                  <input
                    type="file"
                    id="imageFile"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={processingImage}
                  />
                </label>
                {processingImage && (
                  <div className="flex items-center text-primary-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-600 mr-2"></div>
                    <span className="text-sm">Processing image...</span>
                  </div>
                )}
              </div>

              {/* Image Preview and Info */}
              {imagePreview && originalImageDimensions && logoSpacing && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Original Image Preview */}
                    <div className="text-center">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Original Image</h4>
                      <img 
                        src={imagePreview} 
                        alt="Original preview"
                        className="w-full max-w-32 h-32 object-cover rounded-lg border mx-auto"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {originalImageDimensions.width} × {originalImageDimensions.height}px
                      </p>
                      <div className="flex items-center justify-center mt-1">
                        {logoSpacing.isSquare ? (
                          <div className="flex items-center text-xs text-blue-600">
                            <Square className="h-3 w-3 mr-1" />
                            Square Logo
                          </div>
                        ) : (
                          <div className="flex items-center text-xs text-purple-600">
                            <RectangleHorizontal className="h-3 w-3 mr-1" />
                            Rectangular Logo
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex items-center justify-center">
                      <div className="text-gray-400">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>

                    {/* Processed Image Preview */}
                    <div className="text-center">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Banner Optimized</h4>
                      {formData.processedImageUrl ? (
                        <div className="flex justify-center">
                          <img 
                            src={formData.processedImageUrl} 
                            alt="Banner optimized preview"
                            className={`object-cover rounded-lg border ${logoSpacing.imageClass}`}
                          />
                        </div>
                      ) : (
                        <div className={`bg-gray-200 rounded-lg border mx-auto flex items-center justify-center ${logoSpacing.containerClass}`}>
                          <ImageIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {logoSpacing.isSquare ? '80 × 80px' : logoSpacing.aspectRatio > 1.1 ? '112 × 80px' : '64 × 96px'}
                      </p>
                      <p className="text-xs text-green-600 mt-1 font-medium">✓ Adaptive spacing</p>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      <strong>Smart Resize:</strong> Your logo has been automatically detected as {logoSpacing.isSquare ? 'square' : 'rectangular'} and optimized with adaptive spacing for the advertisement banner. The layout will adjust to provide the best visual presentation.
                    </p>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-500">
                {t('advertising.imageRequirements')} - Images will be automatically resized with adaptive spacing based on their aspect ratio.
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
              {t('advertising.duration')} *
            </label>
            <select
              id="duration"
              name="duration"
              value={formData.duration}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600"
              required
            >
              <option value={1}>{t('advertising.oneWeek')} - €{prices.oneWeek}</option>
              <option value={2}>{t('advertising.twoWeeks')} - €{prices.twoWeeks}</option>
              <option value={3}>{t('advertising.threeWeeks')} - €{prices.threeWeeks}</option>
            </select>
          </div>

          {/* Price Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between items-center">
              <span className="font-medium">{t('advertising.totalPrice')}:</span>
              <span className="text-xl font-bold text-primary-600">€{calculatePrice()}</span>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || processingImage}
            className="w-full bg-primary-600 text-white py-3 rounded-md font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('advertising.submitting')}
              </span>
            ) : (
              t('advertising.submitAdvertisement')
            )}
          </button>
        </form>
      </div>

      {/* My Advertisements */}
      {myAds.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-6 flex items-center">
            <Eye className="h-5 w-5 mr-2" />
            {t('advertising.myAdvertisements')}
          </h2>
          
          <div className="space-y-4">
            {myAds.map((ad) => (
              <div key={ad.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-medium">{ad.title}</h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(ad.status)}`}>
                    {t(`advertising.status.${ad.status}`)}
                  </span>
                </div>
                
                <p className="text-gray-600 mb-3">{ad.description}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>{ad.duration} {ad.duration === 1 ? t('advertising.week') : t('advertising.weeks')}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="h-4 w-4 mr-1 flex items-center justify-center text-xs font-bold">€</div>
                    <span>€{ad.totalPrice}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{t('project.created')}: {ad.createdAt.toLocaleDateString()}</span>
                  </div>
                  {ad.startDate && ad.endDate && (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{ad.startDate.toLocaleDateString()} - {ad.endDate.toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertisingPage;